import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import ResmoMascot from "../components/ResmoMascot";
import {
  RK, ReportLabel, ReportList, ReportRichList, ReportGrid, ReportChips,
} from "../components/reports/ReportKit";
import {
  Lock, TrendingUp, GraduationCap, BookOpen, Award, Users, Bot, ExternalLink,
} from "lucide-react";

const searchUrl = (q) => `https://www.google.com/search?q=${encodeURIComponent(q)}`;

export default function PublicRole() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/role/${token}`);
        setData(data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
  }, [token]);

  const tierWidth = useMemo(() => {
    if (!data) return 0;
    const tiers = data.research?.result?.salary_tiers || [];
    return Math.max(...tiers.map((t) => t.high || 0), 1);
  }, [data]);

  if (error) {
    return (
      <div className="min-h-screen rm-paper flex items-center justify-center px-4">
        <div className="text-center" data-testid="role-share-error">
          <Lock size={48} className="mx-auto" />
          <h1 className="font-display text-3xl uppercase mt-3">Link not available</h1>
          <p className="font-mono-rm text-sm mt-2">{error}</p>
          <Link to="/" className="rm-btn rm-btn-primary mt-6 inline-flex">Go to Resmo →</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen rm-paper flex items-center justify-center">
        <div className="text-center font-mono-rm" data-testid="role-share-loading">Loading...</div>
      </div>
    );
  }

  const r = data.research || {};
  const result = r.result || {};
  const overview = result.role_overview || {};
  const skills = result.skills_required || {};
  const tiers = result.salary_tiers || [];
  const career = result.career_path || {};
  const edu = result.education || {};
  const outlook = result.market_outlook || {};

  const fmt = (n) => n ? `${r.currency} ${Math.round(n).toLocaleString()}` : "—";
  const demandColor = ({ hot: "#EF4444", strong: "#20C1A7", steady: "#FFC145", declining: "#94A3B8" })[outlook.demand] || "#94A3B8";

  return (
    <div className="min-h-screen rm-paper">
      <header
        className="px-4 md:px-8 py-4 flex items-center justify-between"
        style={{ borderBottom: "4px solid #0F1B2D", background: "rgba(255,242,236,0.92)" }}
      >
        <Link to="/" className="font-display text-2xl flex items-baseline" style={{ letterSpacing: "-0.02em" }}>
          <span style={{ color: "#FF6A00" }}>R</span><span>esmo</span>
        </Link>
        <Link
          to="/register"
          className="rm-btn rm-btn-primary text-xs px-4 py-2"
          data-testid="role-share-cta"
        >
          Research your own role →
        </Link>
      </header>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 pb-20">
        <div className="rm-tag mb-3" data-testid="role-share-tag">
          <Lock size={12} /> Public role brief{data.shared_by ? ` · shared by ${data.shared_by}` : ""}
        </div>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-4xl md:text-5xl uppercase">{r.role}</h1>
            <div className="font-mono-rm text-sm mt-2 uppercase tracking-widest" style={{ color: "#475569" }}>
              {r.location || "U.S. national average"}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="rm-tag" style={{ background: demandColor, color: "white", borderColor: "#0F1B2D" }}>
              {outlook.demand || "—"} demand
            </span>
            <ResmoMascot state="satisfied" size={110} />
          </div>
        </div>

        {/* Overview */}
        <div className="rm-card-static p-6 md:p-8 mb-6">
          <ReportLabel color={RK.navy} className="mb-1">What this role does</ReportLabel>
          <p className="text-[13px] leading-snug mb-4" style={{ color: RK.body }}>{overview.summary || "—"}</p>
          <ReportChips title="Also called" items={overview.typical_titles} tone="nice" />
        </div>

        {/* Salary tiers */}
        {tiers.length > 0 && (
          <div className="rm-card-static p-6 md:p-8 mb-6" style={{ background: "#0F1B2D", color: "white" }}>
            <ReportLabel color={RK.mustard} className="mb-4">Salary by experience — from entry to expert</ReportLabel>
            <div className="space-y-3">
              {tiers.map((t, i) => {
                const pct = tierWidth ? Math.max(8, (t.high / tierWidth) * 100) : 0;
                const lowPct = tierWidth ? (t.low / tierWidth) * 100 : 0;
                return (
                  <div key={i} className="p-4 rounded-md" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(32,193,167,0.5)" }}>
                    <div className="flex justify-between items-baseline gap-3 mb-2 flex-wrap">
                      <div className="font-display text-lg uppercase">
                        {t.tier} <span className="font-mono-rm text-xs" style={{ color: "#94A3B8" }}>· {t.years} yrs</span>
                      </div>
                      <div className="font-mono-rm text-sm">
                        <span style={{ color: "#94A3B8" }}>{fmt(t.low)}</span> – <strong style={{ color: "#FF6A00" }}>{fmt(t.mid)}</strong> – <span style={{ color: "#94A3B8" }}>{fmt(t.high)}</span>
                      </div>
                    </div>
                    <div className="relative h-3 rounded-full" style={{ background: "rgba(255,255,255,0.1)", border: "1px solid #20C1A7" }}>
                      <div className="absolute h-full rounded-full" style={{
                        left: `${lowPct}%`, width: `${pct - lowPct}%`,
                        background: "linear-gradient(90deg, #FF6A00, #FFC145)",
                      }} />
                    </div>
                    {t.what_changes && <p className="text-sm mt-2" style={{ color: "#CBD5E1" }}>{t.what_changes}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Skills */}
        <div className="rm-card-static p-6 md:p-8 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <ReportRichList
              title="Must-have skills — non-negotiables"
              color={RK.orange}
              items={(skills.must_have || []).map((s) => ({ heading: s.name, body: s.why }))}
            />
            <ReportRichList
              title="Nice-to-have — edge-makers"
              color={RK.slate}
              items={(skills.nice_to_have || []).map((s) => ({ heading: s.name, body: s.why }))}
            />
            <ReportChips title="Tools & platforms" items={skills.tools} tone="teal" className="md:col-span-2" />
          </div>
        </div>

        {/* Career path */}
        {(career.typical_progression || career.lateral_options || career.exit_opportunities) && (
          <div className="rm-card-static p-6 md:p-8 mb-6">
            <ReportLabel color={RK.navy} className="mb-3">Career path — where this can take you</ReportLabel>
            <ReportGrid cols={3}>
              <ReportList title="Progression ladder" color={RK.blue} ordered items={career.typical_progression} />
              <ReportList title="Lateral moves" color={RK.navy} items={career.lateral_options} />
              <ReportList title="Exit opportunities" color={RK.green} items={career.exit_opportunities} />
            </ReportGrid>
          </div>
        )}

        {/* Education */}
        {(edu.degrees || edu.certifications || edu.courses || edu.books || edu.communities) && (
          <div className="rm-card-static p-6 md:p-8 mb-6">
            <ReportLabel icon={<GraduationCap size={11} />} color={RK.navy} className="mb-3">Skill up — build the credentials</ReportLabel>
            <ReportGrid>
              <ReportList
                title="Certifications"
                icon={<Award size={11} />}
                color={RK.green}
                items={edu.certifications}
                renderItem={(c) => (
                  <>
                    <a href={searchUrl(`${c.name} ${c.provider}`)} target="_blank" rel="noopener noreferrer" className="underline font-semibold" style={{ color: RK.navy }}>
                      {c.name}
                    </a>
                    <span style={{ color: RK.sub }}> · {c.provider}{c.level ? ` · ${c.level}` : ""}</span>
                    <ExternalLink size={11} className="inline ml-1" />
                  </>
                )}
              />
              <ReportList
                title="Online courses"
                icon={<BookOpen size={11} />}
                color={RK.purple}
                items={edu.courses}
                renderItem={(c) => (
                  <>
                    <a href={searchUrl(`${c.name} ${c.provider}`)} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: RK.navy }}>
                      {c.name}
                    </a>
                    <span style={{ color: RK.sub }}> · {c.provider}</span>
                  </>
                )}
              />
              <ReportChips title="Communities" icon={<Users size={11} />} items={edu.communities} tone="trending" className="md:col-span-2" />
            </ReportGrid>
          </div>
        )}

        {/* Outlook */}
        {(outlook.trend || outlook.ai_impact || outlook.competitiveness) && (
          <div className="rm-card-static p-6 md:p-8 mb-6">
            <ReportLabel icon={<TrendingUp size={11} />} color={RK.orange} className="mb-3">Market outlook — where this is heading</ReportLabel>
            <ReportGrid>
              {outlook.trend && (
                <div>
                  <ReportLabel color={RK.orange} className="mb-1">Trend</ReportLabel>
                  <p className="text-[13px] leading-snug" style={{ color: RK.body }}>{outlook.trend}</p>
                </div>
              )}
              {outlook.ai_impact && (
                <div>
                  <ReportLabel icon={<Bot size={11} />} color={RK.purple} className="mb-1">AI impact</ReportLabel>
                  <p className="text-[13px] leading-snug" style={{ color: RK.body }}>{outlook.ai_impact}</p>
                </div>
              )}
              {outlook.competitiveness && (
                <div className="md:col-span-2">
                  <ReportLabel color={RK.red} className="mb-1">Competitiveness</ReportLabel>
                  <p className="text-[13px] leading-snug" style={{ color: RK.body }}>{outlook.competitiveness}</p>
                </div>
              )}
            </ReportGrid>
          </div>
        )}

        {/* CTA */}
        <div className="mt-6 p-6 rounded-md" style={{ background: "#0F1B2D", color: "white", border: "3px solid #FF6A00" }}>
          <div className="font-display text-2xl uppercase">Want a brief like this for your own target role?</div>
          <p className="text-sm mt-2" style={{ color: "#CBD5E1" }}>
            Resmo turns any job title into a roadmap — pay tiers, skills, and where it leads. Free to start.
          </p>
          <Link to="/register" className="rm-btn rm-btn-secondary mt-3 inline-flex" data-testid="role-share-bottom-cta">
            Try Resmo free →
          </Link>
        </div>
      </div>
    </div>
  );
}
